import React from "react";
import { CourseResource } from "../../../stores/CoursesStore/Types";
import { OngoingCourse } from "../../../stores/EnrolmentStore/Types";

interface Props {
  course: CourseResource;
  ongoingCourse?: OngoingCourse;
  userIsInUk?: boolean;
}

const courseUrl = (course: CourseResource, ongoingCourse?: OngoingCourse) => {
  if (ongoingCourse) {
    return ongoingCourse.embedded_host_url || `/enrolments/${ongoingCourse.id}`;
  }

  if (course.embedded_host_url) {
    return course.embedded_host_url;
  }

  if (course.enrolment_id) {
    return `/enrolments/${course.enrolment_id}`;
  }

  return course.course_path || `/courses/${course.slug}`;
};

const courseImage = (course: CourseResource, userIsInUk?: boolean) => {
  if (userIsInUk && course.preview_headshot_alt_image_url) {
    return course.preview_headshot_alt_image_url;
  }

  return course.preview_headshot_image_url;
};

const courseDescription = (course: CourseResource) => {
  return course.preview_mini_description || course.tile_description || "";
};

const courseCompletion = (
  course: CourseResource,
  ongoingCourse?: OngoingCourse,
) => {
  const percentageCompleted =
    ongoingCourse?.percentage_completed ??
    course.enrolment_percent_completion ??
    0;

  return Math.max(0, Math.min(100, percentageCompleted));
};

const courseIsEnrolled = (
  course: CourseResource,
  ongoingCourse?: OngoingCourse,
) => {
  return (
    Boolean(ongoingCourse) ||
    Boolean(course.enrolled_student_present) ||
    Boolean(course.enrolment_id)
  );
};

const renderBadge = (course: CourseResource, isEnrolled: boolean) => {
  if (isEnrolled) {
    return <span className="badge enrolled">Enrolled</span>;
  }

  if (course.content_version === "upcoming") {
    return <span className="badge coming">Coming Soon</span>;
  }

  if (course.sample) {
    return <span className="badge free">Free Sample</span>;
  }

  // Ticked in admin for a course given a big update for the AI age. A choice
  // someone made, so it wins over New and Bestseller, which are worked out.
  if (course.rebuilt_for_ai) {
    return <span className="badge rebuilt">Rebuilt for AI</span>;
  }

  if (course.new_course || course.went_live_in_the_past_month) {
    return <span className="badge new">New</span>;
  }

  if (course.best_seller) {
    return <span className="badge bestseller">Bestseller</span>;
  }

  return null;
};

const CourseTile = ({ course, ongoingCourse, userIsInUk }: Props) => {
  const image = courseImage(course, userIsInUk);
  const isEnrolled = courseIsEnrolled(course, ongoingCourse);
  const completion = courseCompletion(course, ongoingCourse);

  return (
    <a
      href={courseUrl(course, ongoingCourse)}
      className={`course-card ${isEnrolled ? "course-card--enrolled" : ""}`}
      data-name={course.name}
    >
      <div className="course-cover">
        {image ? (
          <img
            src={image}
            alt={course.name}
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className="cover-placeholder">[ Image ]</div>
        )}
        {renderBadge(course, isEnrolled)}
      </div>

      <div className="course-body">
        <h3 className="course-title">{course.name}</h3>
        <p className="course-desc">{courseDescription(course)}</p>

        {isEnrolled && (
          <div className="course-progress-row">
            <div
              aria-label={`${completion}% complete`}
              className="course-progress"
            >
              <span style={{ width: `${completion}%` }} />
            </div>
            <span className="course-progress-pct">{completion}%</span>
          </div>
        )}

        <div className="course-meta">
          {course.content_version === "upcoming" ? (
            <span className="course-instructor">Pre-register</span>
          ) : isEnrolled ? (
            <span className="course-instructor">
              {completion === 100 ? "Completed" : "In progress"}
            </span>
          ) : (
            <span className="course-rating">
              {Number(course.rating || 0).toFixed(1)}
            </span>
          )}

          <span className="course-cta">
            {isEnrolled
              ? completion === 100
                ? "View again"
                : "Resume"
              : "More info →"}
          </span>
        </div>
      </div>
    </a>
  );
};

export default CourseTile;
